/**
 * Operator-facing "order needs an action" presentation.
 * Pure: does not create refunds, cancel quantity, or write providers.
 */
import { providerDisplayName } from "./order-provenance";
import { refundedUnfulfilledCancellableQty } from "./reverse-lifecycle";
import { ebayInboundCancelIsActionableBuyer } from "./inbound-buyer-cancel";
import { EBAY_INBOUND_REQUEST_KINDS, ebayInboundEventIsActionable, ebayInboundIssueSpec, type EbayInboundRequestKind } from "./inbound-after-sales";

export type OrderAttentionKind =
  | "refund_sync"
  | "cancel_refunded_unfulfilled"
  | "tracking_sync"
  | "cancellation_sync"
  | "buyer_cancellation"
  | "buyer_return"
  | "buyer_inr"
  | "buyer_snad"
  | "ebay_case"
  | "payment_dispute"
  | "return_disposition";

export type OrderAttentionTone = "pending" | "warning" | "danger";

export interface OrderAttentionItem {
  id: string;
  kind: OrderAttentionKind;
  tone: OrderAttentionTone;
  title: string;
  body: string;
  icon: string;
  primaryLabel: string;
  primaryDestructive: boolean;
  secondaryLabel: string | null;
  refundId: string | null;
}

export interface OrderAttentionRefundInput {
  id: string;
  provider_sync_status: string | null | undefined;
}

export interface OrderAttentionRefundItemInput {
  sales_refund_id: string;
  sales_order_item_id: string;
  qty: number;
  qty_unfulfilled_cancelled?: number | null;
}

export interface OrderAttentionLineInput {
  id: string;
  qty_ordered: number;
  qty_fulfilled: number;
  qty_cancelled?: number | null;
  qty_allocated: number;
}

export function channelRefundSyncCopy(providerName: string): { title: string; body: string; primaryLabel: string } {
  const channel = providerName.trim() || "the channel";
  return {
    title: "Refund needs channel sync",
    body: `The refund is recorded in Tradivo but has not yet been confirmed by ${channel}.`,
    primaryLabel: "Retry refund sync",
  };
}

export function refundedReservedCopy(qty: number): { title: string; body: string; primaryLabel: string; secondaryLabel: string } {
  const n = Math.max(0, Math.floor(qty));
  const itemWord = n === 1 ? "item is" : "items are";
  return {
    title: "Refunded item still reserved",
    body: `${n} refunded ${itemWord} still reserved for fulfilment. Cancel the refunded quantity if it should no longer be shipped.`,
    primaryLabel: "Cancel refunded quantity",
    secondaryLabel: "Keep reserved for fulfilment",
  };
}

export function refundNeedsChannelSync(status: string | null | undefined): boolean {
  return status === "pending" || status === "error";
}

export function buildRefundSyncAttention(opts: {
  refundId: string;
  providerSyncStatus: string | null | undefined;
  provider: string | null | undefined;
}): OrderAttentionItem | null {
  if (!refundNeedsChannelSync(opts.providerSyncStatus)) return null;
  const copy = channelRefundSyncCopy(providerDisplayName(opts.provider));
  return {
    id: `refund-sync:${opts.refundId}`,
    kind: "refund_sync",
    tone: opts.providerSyncStatus === "error" ? "warning" : "pending",
    title: copy.title,
    body: copy.body,
    icon: opts.providerSyncStatus === "error" ? "ri-error-warning-line" : "ri-time-line",
    primaryLabel: copy.primaryLabel,
    primaryDestructive: false,
    secondaryLabel: null,
    refundId: opts.refundId,
  };
}

export function refundedUnfulfilledReservedQty(opts: {
  refundItems: OrderAttentionRefundItemInput[];
  items: OrderAttentionLineInput[];
  refundId: string;
}): number {
  return opts.refundItems
    .filter((ri) => ri.sales_refund_id === opts.refundId)
    .reduce((sum, ri) => {
      const item = opts.items.find((it) => it.id === ri.sales_order_item_id);
      if (!item) return sum;
      const eligible = refundedUnfulfilledCancellableQty({
        qtyOrdered: item.qty_ordered,
        qtyFulfilled: item.qty_fulfilled,
        qtyCancelled: item.qty_cancelled ?? 0,
        qtyRefunded: ri.qty,
        qtyAlreadyCancelledFromRefund: ri.qty_unfulfilled_cancelled ?? 0,
      });
      const reserved = Math.min(eligible, Math.max(0, Math.floor(item.qty_allocated || 0)));
      return sum + reserved;
    }, 0);
}

export function buildRefundedReservedAttention(opts: {
  refundId: string;
  reservedQty: number;
  acknowledged: boolean;
}): OrderAttentionItem | null {
  if (opts.acknowledged || opts.reservedQty <= 0) return null;
  const copy = refundedReservedCopy(opts.reservedQty);
  return {
    id: `refunded-reserved:${opts.refundId}`,
    kind: "cancel_refunded_unfulfilled",
    tone: "danger",
    title: copy.title,
    body: copy.body,
    icon: "ri-inbox-unarchive-line",
    primaryLabel: copy.primaryLabel,
    primaryDestructive: true,
    secondaryLabel: copy.secondaryLabel,
    refundId: opts.refundId,
  };
}

export function channelCancellationSyncCopy(providerName: string): { title: string; body: string; primaryLabel: string } {
  const channel = providerName.trim() || "the channel";
  return {
    title: "Cancellation needs channel confirmation",
    body: `Tradivo cancelled remaining quantity, but ${channel} has not confirmed the cancellation yet.`,
    primaryLabel: "Retry cancellation sync",
  };
}

export function buildCancellationSyncAttention(opts: {
  orderStatus: string;
  providerCancelStatus: string | null | undefined;
  provider: string | null | undefined;
}): OrderAttentionItem | null {
  if (opts.orderStatus !== "cancelled") return null;
  if (opts.providerCancelStatus === "confirmed" || opts.providerCancelStatus === "not_applicable") return null;
  const copy = channelCancellationSyncCopy(providerDisplayName(opts.provider));
  return {
    id: "cancellation-sync",
    kind: "cancellation_sync",
    tone: opts.providerCancelStatus === "error" ? "warning" : "pending",
    title: copy.title,
    body: copy.body,
    icon: opts.providerCancelStatus === "error" ? "ri-error-warning-line" : "ri-time-line",
    primaryLabel: copy.primaryLabel,
    primaryDestructive: false,
    secondaryLabel: null,
    refundId: null,
  };
}

export function buildBuyerCancellationAttention(opts: {
  inboundCancelId?: string | null;
  initiator?: string | null;
  reason?: string | null;
  requestState?: string | null;
  providerOrderCancelState?: string | null;
  providerFulfillmentStatus?: string | null;
  localOrderStatus?: string | null;
}): OrderAttentionItem | null {
  if (!opts.inboundCancelId) return null;
  if (!ebayInboundCancelIsActionableBuyer({
    initiator: opts.initiator,
    requestState: opts.requestState,
    orderCancelState: opts.providerOrderCancelState,
  })) return null;
  const reason = (opts.reason ?? "").trim();
  const state = (opts.requestState ?? "REQUESTED").trim() || "REQUESTED";
  return {
    id: `buyer-cancellation:${opts.inboundCancelId}`,
    kind: "buyer_cancellation",
    tone: "warning",
    title: "Buyer requested cancellation",
    body: [
      reason ? `Reason: ${reason}` : null,
      `Request ${opts.inboundCancelId} · ${state}`,
      opts.initiator ? `Initiator: ${opts.initiator}` : null,
      `Tradivo fulfilment: ${(opts.localOrderStatus ?? "").trim().toUpperCase() || "unknown"}. eBay fulfilment: ${opts.providerFulfillmentStatus || "unknown"}. Seller Accept/Decline stays on eBay until an operator action exists.`,
    ].filter(Boolean).join(" "),
    icon: "ri-user-unfollow-line",
    primaryLabel: "Open Operational Issues",
    primaryDestructive: false,
    secondaryLabel: null,
    refundId: null,
  };
}

export function buildInboundAfterSalesAttention(opts: {
  requestKind: string;
  providerRequestId: string;
  initiator?: string | null;
  reason?: string | null;
  requestState?: string | null;
  providerOrderCancelState?: string | null;
  providerFulfillmentStatus?: string | null;
  localOrderStatus?: string | null;
}): OrderAttentionItem | null {
  const kind = opts.requestKind as EbayInboundRequestKind;
  if (!EBAY_INBOUND_REQUEST_KINDS.includes(kind)) return null;
  if (kind === "buyer_cancellation") {
    return buildBuyerCancellationAttention({
      inboundCancelId: opts.providerRequestId,
      initiator: opts.initiator,
      reason: opts.reason,
      requestState: opts.requestState,
      providerOrderCancelState: opts.providerOrderCancelState,
      providerFulfillmentStatus: opts.providerFulfillmentStatus,
      localOrderStatus: opts.localOrderStatus,
    });
  }
  if (!ebayInboundEventIsActionable({
    kind,
    initiator: opts.initiator,
    requestState: opts.requestState,
    orderCancelState: opts.providerOrderCancelState,
  })) return null;
  const spec = ebayInboundIssueSpec(kind);
  const reason = (opts.reason ?? "").trim();
  const state = (opts.requestState ?? "OPEN").trim() || "OPEN";
  return {
    id: `inbound:${kind}:${opts.providerRequestId}`,
    kind: kind as OrderAttentionKind,
    tone: "warning",
    title: spec.title,
    body: [
      reason ? `Reason: ${reason}` : null,
      `${opts.providerRequestId} · ${state}`,
      `Tradivo fulfilment: ${(opts.localOrderStatus ?? "").trim().toUpperCase() || "unknown"}. eBay fulfilment: ${opts.providerFulfillmentStatus || "unknown"}. Provider response stays on eBay until an operator action exists.`,
    ].filter(Boolean).join(" "),
    icon: "ri-alarm-warning-line",
    primaryLabel: "Open Operational Issues",
    primaryDestructive: false,
    secondaryLabel: null,
    refundId: null,
  };
}

export function buildSalesOrderAttentionItems(opts: {
  provider: string | null | undefined;
  refunds: OrderAttentionRefundInput[];
  refundItems: OrderAttentionRefundItemInput[];
  items: OrderAttentionLineInput[];
  acknowledgedKeepReservedIds?: readonly string[];
  orderStatus?: string | null;
  providerCancelStatus?: string | null;
  inboundCancelId?: string | null;
  inboundInitiator?: string | null;
  inboundReason?: string | null;
  inboundRequestState?: string | null;
  inboundProviderOrderCancelState?: string | null;
  inboundProviderFulfillmentStatus?: string | null;
  inboundEvents?: Array<{
    request_kind: string;
    provider_request_id: string;
    initiator?: string | null;
    reason?: string | null;
    request_state?: string | null;
    provider_order_cancel_state?: string | null;
    provider_fulfillment_status?: string | null;
  }>;
}): OrderAttentionItem[] {
  const acknowledged = new Set(opts.acknowledgedKeepReservedIds ?? []);
  const items: OrderAttentionItem[] = [];
  if (opts.inboundEvents && opts.inboundEvents.length > 0) {
    for (const event of opts.inboundEvents) {
      const item = buildInboundAfterSalesAttention({
        requestKind: event.request_kind,
        providerRequestId: event.provider_request_id,
        initiator: event.initiator,
        reason: event.reason,
        requestState: event.request_state,
        providerOrderCancelState: event.provider_order_cancel_state,
        providerFulfillmentStatus: event.provider_fulfillment_status ?? opts.inboundProviderFulfillmentStatus,
        localOrderStatus: opts.orderStatus,
      });
      if (item) items.push(item);
    }
  } else {
    const buyerCancel = buildBuyerCancellationAttention({
      inboundCancelId: opts.inboundCancelId,
      initiator: opts.inboundInitiator,
      reason: opts.inboundReason,
      requestState: opts.inboundRequestState,
      providerOrderCancelState: opts.inboundProviderOrderCancelState,
      providerFulfillmentStatus: opts.inboundProviderFulfillmentStatus,
      localOrderStatus: opts.orderStatus,
    });
    if (buyerCancel) items.push(buyerCancel);
  }
  const cancelSync = buildCancellationSyncAttention({
    orderStatus: opts.orderStatus ?? "",
    providerCancelStatus: opts.providerCancelStatus,
    provider: opts.provider,
  });
  if (cancelSync) items.push(cancelSync);
  for (const refund of opts.refunds) {
    const sync = buildRefundSyncAttention({
      refundId: refund.id,
      providerSyncStatus: refund.provider_sync_status,
      provider: opts.provider,
    });
    if (sync) items.push(sync);
    const reservedQty = refundedUnfulfilledReservedQty({
      refundItems: opts.refundItems,
      items: opts.items,
      refundId: refund.id,
    });
    const reserved = buildRefundedReservedAttention({
      refundId: refund.id,
      reservedQty,
      acknowledged: acknowledged.has(refund.id),
    });
    if (reserved) items.push(reserved);
  }
  return items;
}

/** Current 8640 production fixture: confirmed refund + cancelled unfulfilled qty. */
export const LIVE_8640_ATTENTION_FIXTURE = {
  provider: "bigcommerce",
  refunds: [{ id: "4a4ef233-b0af-42d9-8e0a-cc37b18f624e", provider_sync_status: "confirmed" as const }],
  refundItems: [{
    sales_refund_id: "4a4ef233-b0af-42d9-8e0a-cc37b18f624e",
    sales_order_item_id: "d3b72ec9-27b6-4410-b749-f739d68610e8",
    qty: 1,
    qty_unfulfilled_cancelled: 1,
  }],
  items: [{
    id: "d3b72ec9-27b6-4410-b749-f739d68610e8",
    qty_ordered: 2,
    qty_fulfilled: 0,
    qty_cancelled: 1,
    qty_allocated: 1,
  }],
};
